import { useRef } from "react";
import type { CsChatAttachmentTokenGetter } from "./attachments";

/** client JWT の TTL は 15 分。失効 60 秒前までキャッシュを再利用する。 */
const EXPIRY_MARGIN_MS = 60_000;

type CachedToken = { token: string; expiresAt: number };

/** Base64URL の 1 セグメントを UTF-8 文字列に decode する。 */
const decodeBase64Url = (segment: string): string => {
  const base64 = segment.replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64.padEnd(
    base64.length + ((4 - (base64.length % 4)) % 4),
    "=",
  );
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  return new TextDecoder().decode(bytes);
};

/**
 * JWT payload から `exp`(秒) を取り出す。形式不正・decode 失敗時は null を返し、
 * 呼び出し側はキャッシュせず都度発行にフォールバックする。
 */
export const decodeJwtExp = (token: string): number | null => {
  const segment = token.split(".")[1];
  if (!segment) {
    return null;
  }

  try {
    const payload = JSON.parse(decodeBase64Url(segment)) as { exp?: unknown };
    return typeof payload.exp === "number" ? payload.exp : null;
  } catch {
    return null;
  }
};

/**
 * exp を見て失効前まで client JWT をキャッシュする getter を作る。複数の呼び出しが
 * 同時に発火してもキャッシュ未成立の間は in-flight の 1 リクエストに束ねる
 * （呼び出しごとに mutation を撃たないため）。decode に失敗した token はキャッシュ
 * しない。`forceRefresh` はキャッシュ・in-flight を無視して都度発行する
 * （401 の 1 回再試行用）。添付 I/O と未読数 Provider の両方で使う汎用 getter で、
 * React 非依存で単体テスト可能。
 */
export const createCsChatClientTokenGetter = ({
  createToken,
  now = () => Date.now(),
}: {
  createToken: () => Promise<string>;
  now?: () => number;
}): CsChatAttachmentTokenGetter => {
  let cached: CachedToken | null = null;
  let inFlight: Promise<string> | null = null;

  return async ({ forceRefresh = false } = {}) => {
    if (!forceRefresh && cached && cached.expiresAt > now()) {
      return cached.token;
    }

    if (!forceRefresh && inFlight) {
      return inFlight;
    }

    const request = (async () => {
      const token = await createToken();
      const exp = decodeJwtExp(token);
      cached =
        exp === null
          ? null
          : { token, expiresAt: exp * 1000 - EXPIRY_MARGIN_MS };
      return token;
    })();

    inFlight = request;
    try {
      return await request;
    } finally {
      if (inFlight === request) {
        inFlight = null;
      }
    }
  };
};

/**
 * 添付 I/O 用の client token を exp キャッシュ付きで返す hook。WS 接続用の token
 * 取得（`agents/react` の `cacheTtl`）とは独立。
 */
export const useCsChatAttachmentToken = (
  createToken: () => Promise<string>,
): CsChatAttachmentTokenGetter => {
  const createTokenRef = useRef(createToken);
  createTokenRef.current = createToken;

  const getterRef = useRef<CsChatAttachmentTokenGetter | null>(null);
  if (getterRef.current === null) {
    getterRef.current = createCsChatClientTokenGetter({
      createToken: () => createTokenRef.current(),
    });
  }

  return getterRef.current;
};
